import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import "./SpeedTest.css";

const SpeedTest = () => {
  const [provider, setProvider] = useState({ provider: "", ip_address: "" });
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState(null);

  const [isRunning, setIsRunning] = useState(false);
  const [status, setStatus] = useState("Idle");

  const [download, setDownload] = useState(null);
  const [upload, setUpload] = useState(null);
  const [ping, setPing] = useState(null);

  const arcRef = useRef(null);

  useEffect(() => {
    const initData = async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/diagnostic-tools/provider-info`),
          axios.get(`${API_BASE_URL}/diagnostic-tools/server-list`)
        ]);
        setProvider(pRes.data);
        setServers(sRes.data || []);
        if (sRes.data && sRes.data.length > 0) {
          setSelectedServer(sRes.data[0]);
        }
      } catch (err) {
        console.error("Initialization failed", err);
      }
    };
    initData();
  }, []);

  const setArcProgress = (mbps) => {
    if (!arcRef.current) return;
    const percent = Math.min(1, mbps / 500); // Scale up to 500 Mbps
    const length = arcRef.current.getTotalLength();
    const dash = length * percent;
    arcRef.current.style.strokeDasharray = `${dash} ${length - dash}`;
  };

  // --- Ping Measurement ---
  const measurePing = async (baseUrl) => {
    const pings = [];
    for (let i = 0; i < 5; i++) {
      const start = performance.now();
      try {
        await fetch(`${baseUrl}/empty?r=${Math.random()}`, { cache: "no-store" });
        pings.push(performance.now() - start);
      } catch (e) {}
    }
    return pings.length ? Math.round(pings.reduce((a, b) => a + b, 0) / pings.length) : 0;
  };

  // --- Parallel Stream Download (Accurate Speed) ---
  const measureDownload = (baseUrl) => {
    return new Promise((resolve) => {
      const threadCount = 3;
      let loadedBytes = new Array(threadCount).fill(0);
      const startTime = performance.now();
      let completed = 0;

      for (let i = 0; i < threadCount; i++) {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${baseUrl}/garbage?r=${Math.random()}_${i}`, true);
        xhr.responseType = "arraybuffer";

        xhr.onprogress = (e) => {
          if (e.lengthComputable) {
            loadedBytes[i] = e.loaded;
            const totalLoaded = loadedBytes.reduce((a, b) => a + b, 0);
            const duration = (performance.now() - startTime) / 1000;
            if (duration > 0) {
              const mbps = ((totalLoaded * 8) / duration / 1e6).toFixed(2);
              setDownload(mbps);
              setArcProgress(mbps);
            }
          }
        };

        xhr.onload = () => {
          completed++;
          if (completed === threadCount) {
            const totalLoaded = loadedBytes.reduce((a, b) => a + b, 0);
            const duration = (performance.now() - startTime) / 1000;
            const finalMbps = ((totalLoaded * 8) / duration / 1e6).toFixed(2);
            resolve(finalMbps);
          }
        };

        xhr.onerror = () => resolve(0);
        xhr.send();
      }
    });
  };

  // --- Parallel Stream Upload ---
  const measureUpload = (baseUrl) => {
    return new Promise((resolve) => {
      const threadCount = 2;
      const chunkSize = 10 * 1024 * 1024; // 10 MB per thread
      let loadedBytes = new Array(threadCount).fill(0);
      const startTime = performance.now();
      let completed = 0;

      for (let i = 0; i < threadCount; i++) {
        const dummyData = new Uint8Array(chunkSize);
        const xhr = new XMLHttpRequest();

        xhr.open("POST", `${baseUrl}/upload?r=${Math.random()}_${i}`, true);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            loadedBytes[i] = e.loaded;
            const totalLoaded = loadedBytes.reduce((a, b) => a + b, 0);
            const duration = (performance.now() - startTime) / 1000;
            if (duration > 0) {
              const mbps = ((totalLoaded * 8) / duration / 1e6).toFixed(2);
              setUpload(mbps);
            }
          }
        };

        xhr.onload = () => {
          completed++;
          if (completed === threadCount) {
            const totalLoaded = loadedBytes.reduce((a, b) => a + b, 0);
            const duration = (performance.now() - startTime) / 1000;
            const finalMbps = ((totalLoaded * 8) / duration / 1e6).toFixed(2);
            resolve(finalMbps);
          }
        };

        xhr.onerror = () => resolve(0);
        xhr.send(dummyData);
      }
    });
  };

  const startTest = async () => {
    if (isRunning || !selectedServer) return;

    setIsRunning(true);
    setDownload(0);
    setUpload(0);
    setPing(0);

    const targetUrl = selectedServer.url;

    try {
      setStatus("Testing Ping...");
      const pingVal = await measurePing(targetUrl);
      setPing(pingVal);

      setStatus("Testing Download...");
      const dlVal = await measureDownload(targetUrl);
      setDownload(dlVal);

      setStatus("Testing Upload...");
      const ulVal = await measureUpload(targetUrl);
      setUpload(ulVal);

      setStatus("Completed");
    } catch (e) {
      setStatus("Error");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="ospeed-root">
      <div className="ospeed-card">
        <div className="ospeed-top">
          <div className="brand">n-tools Speedtest</div>
          <div className="provider">{provider.provider || provider.ip_address}</div>
        </div>

        <div className="ospeed-dial-wrap">
          <svg className="ospeed-dial" viewBox="0 0 220 220">
            <defs>
              <linearGradient id="gradC" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff66b3" />
                <stop offset="100%" stopColor="#8a2be2" />
              </linearGradient>
            </defs>
            <circle cx="110" cy="110" r="84" className="ospeed-ring-bg" />
            <path
              ref={arcRef}
              className="ospeed-arc"
              d="M 110 24 A 86 86 0 1 1 109.999 24"
              style={{ stroke: "url(#gradC)" }}
            />
          </svg>
          <div className="ospeed-center">
            <div className="ospeed-number">
              {download === null ? "—" : download}
              <span>Mbps</span>
            </div>
          </div>
        </div>

        <div className="ospeed-metrics">
          <div className="metric-box">
            <span className="metric-label">Upload</span>
            <span className="metric-val">{upload === null ? "—" : `${upload} Mbps`}</span>
          </div>
          <div className="metric-box">
            <span className="metric-label">Ping</span>
            <span className="metric-val">{ping === null ? "—" : `${ping} ms`}</span>
          </div>
        </div>

        <div className="ospeed-controls">
          <select
            value={selectedServer?.id || ""}
            onChange={(e) => setSelectedServer(servers.find((s) => s.id === e.target.value))}
          >
            {servers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.location})
              </option>
            ))}
          </select>

          <button onClick={startTest} disabled={isRunning}>
            {isRunning ? "Testing..." : "Start"}
          </button>
        </div>

        <div className="status">{status}</div>
      </div>
    </div>
  );
};

export default SpeedTest;